import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canAdjustInventory } from '../../lib/permissions';
import { requestRoute } from '../../lib/branchTransfers';
import { getStockStatus } from '../../lib/stockStatus';
import {
  IconPackage,
  IconMinus,
  IconPlus,
  IconAdjustmentsHorizontal,
  IconListDetails,
  IconChevronRight,
  IconArrowLeft,
  IconAlertTriangle,
  IconSend,
} from '@tabler/icons-react';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

export default function InventoryScanSheet() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing } = useAuth();
  // #369 — passes the whole auth object, not the manager triple. See permissions.js.
  const canAdjust = canAdjustInventory({ isAdmin, isManager, isExecutive, isTeamMember, isPurchasing, profile });

  const userBranchId = profile?.branch_id;
  const branchName = BRANCHES.find(b => b.id === userBranchId)?.name || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [stock, setStock] = useState(null);
  const [inboundRequest, setInboundRequest] = useState(null);
  // #684, Session 79.22b.4 -- where Request goes from this label: to the
  // branch that makes it, or to Purchasing. Only the words change here; the
  // request screen the button opens does the routing, so a failed read of the
  // tree leaves the old words and the right sheet still opens.
  const [requestRt, setRequestRt] = useState({ route: 'buy', makerBranchId: null });
  const makerName = BRANCHES.find(b => String(b.id) === String(requestRt.makerBranchId))?.name || 'that branch';

  const load = useCallback(async () => {
    if (!itemId || !userBranchId) return;
    setLoading(true);
    setError('');
    try {
      const { data: itemData, error: itemErr } = await supabase
        .from('inventory_items')
        .select('id, name, sku, uom, is_active, category_id')
        .eq('id', itemId)
        .single();
      if (itemErr || !itemData) { setError('Item not found.'); setLoading(false); return; }
      if (!itemData.is_active) { setError('This item has been deactivated.'); setLoading(false); return; }
      setItem(itemData);

      const { data: cats } = await supabase
        .from('inventory_categories')
        .select('id, parent_id, manufactured_at_branch_id, manufacturing_department_id');
      setRequestRt(requestRoute({ categories: cats || [], categoryId: itemData.category_id, viewerBranchId: userBranchId }));

      const { data: stockData } = await supabase
        .from('inventory_stock')
        .select('id, qty_on_hand, reorder_point')
        .eq('item_id', itemId)
        .eq('branch_id', userBranchId)
        .single();
      setStock(stockData || null);

      const { data: reqData } = await supabase
        .from('material_requests')
        .select(`
          id, request_number, status,
          material_request_items ( id, item_id, qty_requested )
        `)
        .eq('branch_id', userBranchId)
        .eq('status', 'ordered')
        .order('created_at', { ascending: false });

      // #688, Session 79.22b.4 -- `itemId` comes from the web address, so it is
      // TEXT ('29'), and the database's item_id is a NUMBER (29). Strict
      // equality between the two is always false, so the "Inbound shipment
      // waiting" banner below had never once appeared. Compared as text.
      const matched = (reqData || []).find(req =>
        req.material_request_items?.some(mri => String(mri.item_id) === String(itemId))
      );
      if (matched) {
        const mri = matched.material_request_items.find(m => String(m.item_id) === String(itemId));
        setInboundRequest({
          request_number: matched.request_number,
          request_id: matched.id,
          qty_requested: mri?.qty_requested,
        });
      } else {
        setInboundRequest(null);
      }
    } catch (e) {
      setError('Failed to load item.');
    } finally {
      setLoading(false);
    }
  }, [itemId, userBranchId]);

  useEffect(() => { load(); }, [load]);

  function goAction(mode, preloadReceive = false) {
    navigate(`/?scan_action=${mode}&scan_item=${itemId}${preloadReceive && inboundRequest ? '&scan_req=' + inboundRequest.request_id : ''}`);
  }

  function goDetail() {
    navigate(`/?scan_action=detail&scan_item=${itemId}`);
  }

  function goBack() {
    navigate(-1);
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: 13, color: '#9A9A9A' }}>Loading item...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F5F3', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 24px' }}>
        <div style={{ textAlign: 'center' }}>
          <IconAlertTriangle size={32} color="#A32D2D" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F', marginBottom: 6 }}>{error}</div>
          <div style={{ fontSize: 13, color: '#9A9A9A', marginBottom: 20 }}>This QR code may be outdated or invalid.</div>
          <button onClick={goBack} style={{ background: '#0F0F0F', color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const qtyOnHand = stock?.qty_on_hand ?? null;
  const qtyDisplay = qtyOnHand === null ? 'No stock record' : `${qtyOnHand} on hand`;
  // #511 -- the colour asks the shared rule rather than spelling its own, so it
  // cannot drift from Stock Home's Out and Low. Same colours as before.
  const scanStatus = qtyOnHand === null ? null : getStockStatus(qtyOnHand, stock?.reorder_point ?? null);
  const qtyColor = qtyOnHand === null ? '#9A9A9A' : scanStatus === 'Out' ? '#A32D2D' : scanStatus === 'Low' ? '#9A5F00' : '#0F6E56';

  return (
    <div style={{ minHeight: '100vh', background: '#F5F5F3', display: 'flex', flexDirection: 'column' }}>

      <div style={{ background: '#0F0F0F', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A9A9A', padding: 0, display: 'flex', alignItems: 'center' }}>
          <IconArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item?.name}
          </div>
          <div style={{ fontSize: 11, color: '#9A9A9A' }}>
            {item?.sku ? `SKU ${item.sku}` : 'No SKU'} · {item?.uom} · {branchName}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: '14px 14px 32px', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {inboundRequest && (
          <div style={{ background: '#FAEEDA', border: '0.5px solid #EF9F27', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <IconPackage size={20} color="#633806" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#412402' }}>Inbound shipment waiting</div>
              <div style={{ fontSize: 11, color: '#633806', marginTop: 1 }}>
                {inboundRequest.request_number} · {inboundRequest.qty_requested} {item?.uom} requested
              </div>
            </div>
            <button
              onClick={() => goAction('receive', true)}
              style={{ background: '#633806', color: '#FAEEDA', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Receive
            </button>
          </div>
        )}

        <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4, paddingLeft: 2 }}>
          What do you want to do?
        </div>

        <button
          onClick={() => goAction('use')}
          style={{ background: 'white', border: '0.5px solid #E0E0E0', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}
        >
          <div style={{ width: 38, height: 38, background: '#E1F5EE', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconMinus size={18} color="#0F6E56" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>Use stock</div>
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Log material pulled for a job</div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: qtyColor, flexShrink: 0 }}>{qtyDisplay}</div>
        </button>

        <button
          onClick={() => goAction('receive')}
          style={{ background: 'white', border: '0.5px solid #E0E0E0', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}
        >
          <div style={{ width: 38, height: 38, background: '#E6F1FB', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconPlus size={18} color="#185FA5" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>Receive stock</div>
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Check in a delivery or transfer</div>
          </div>
          <IconChevronRight size={16} color="#9A9A9A" />
        </button>

        {/* #684 H -- a made item's label says who makes it and where the ask
            goes. The button still opens the request screen, which opens the
            Ask sheet (or Make these ourselves at the maker). */}
        <button
          onClick={() => goAction('request')}
          style={{ background: 'white', border: '0.5px solid #E0E0E0', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}
        >
          <div style={{ width: 38, height: 38, background: requestRt.route === 'buy' ? '#EEEDFE' : '#E1F5EE', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconSend size={18} color={requestRt.route === 'buy' ? '#534AB7' : '#0F6E56'} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>
              {requestRt.route === 'ask_maker' ? `Ask ${makerName} for more`
                : requestRt.route === 'make_own' ? 'Make these ourselves'
                  : 'Request restock'}
            </div>
            <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>
              {requestRt.route === 'ask_maker' ? `${makerName} makes this. Your ask goes to ${makerName}'s shop`
                : requestRt.route === 'make_own' ? 'We make this. Asks our own shop to make more'
                  : 'Submit a reorder to your manager'}
            </div>
          </div>
          <IconChevronRight size={16} color="#9A9A9A" />
        </button>

        {canAdjust && (
          <button
            onClick={() => goAction('adjust')}
            style={{ background: 'white', border: '0.5px solid #E0E0E0', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <div style={{ width: 38, height: 38, background: '#F5F5F3', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconAdjustmentsHorizontal size={18} color="#5C5C5C" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F0F' }}>Adjust count</div>
              <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Correct a physical count discrepancy</div>
            </div>
            <div style={{ fontSize: 10, background: '#F0F0F0', color: '#5C5C5C', padding: '2px 8px', borderRadius: 10, border: '0.5px solid #E0E0E0', flexShrink: 0 }}>
              Manager+
            </div>
          </button>
        )}

        <button
          onClick={goDetail}
          style={{ background: 'none', border: 'none', padding: '12px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer', marginTop: 4 }}
        >
          <IconListDetails size={14} color="#7A7A7A" />
          <span style={{ fontSize: 12, color: '#7A7A7A' }}>View full item detail</span>
          <IconChevronRight size={13} color="#7A7A7A" />
        </button>

      </div>
    </div>
  );
}